import { useEffect, useRef, useCallback } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'

interface ApiResponse<T> {
  data: T[]
  totalCount: number
}

interface InfiniteLoadingOptions<T> {
  queryKey: unknown[]
  queryFn: (params: { pageParam: number }) => Promise<ApiResponse<T>>
  initialPageParam?: number
  getNextPageParam?: (
    lastPage: ApiResponse<T>,
    pages: ApiResponse<T>[]
  ) => number | undefined
  enabled?: boolean
}

interface InfiniteLoadingResult<T> {
  data: T[]
  totalCount: number
  isLoading: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  loadMoreRef: (node?: Element | null) => void
}

const DEFAULT_PAGE_SIZE = 10

export function useInfiniteLoading<T>({
  queryKey,
  queryFn,
  initialPageParam = 1,
  getNextPageParam = (lastPage, pages) => {
    // If the last page has fewer items than the page size, we've reached the end
    if (lastPage.data.length < DEFAULT_PAGE_SIZE) {
      return undefined
    }
    return pages.length + 1
  },
  enabled = true,
}: InfiniteLoadingOptions<T>): InfiniteLoadingResult<T> {
  const observer = useRef<IntersectionObserver>(null)

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam,
    getNextPageParam,
    enabled,
  })

  const loadMoreRef = useCallback(
    (node?: Element | null) => {
      if (isLoading || isFetchingNextPage) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        {
          rootMargin: '200px',
        }
      )

      if (node) observer.current.observe(node)
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [])

  const flattenedData = data?.pages.flatMap((page) => page.data) ?? []
  const totalCount = data?.pages[0]?.totalCount ?? 0

  return {
    data: flattenedData,
    totalCount,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    error: error as Error | null,
    loadMoreRef,
  }
}
